import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiX } from "react-icons/fi";
import { usePublicLang } from "../../../config/i18n/public";
import { trackAnalyticsEvent } from "../../../utils/analytics";

const API = process.env.REACT_APP_API_URL;

export default function WaitlistModal({ open, onClose }) {
  const { copy, lang } = usePublicLang();
  const t = copy.familyLanding.waitlist;
  const [email, setEmail] = useState("");
  const [launchConsent, setLaunchConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open) return;
    function onKey(event) {
      if (event.key === "Escape") onClose?.();
    }
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setLaunchConsent(false);
      setStatus("idle");
      setMessage("");
    }
  }, [open]);

  if (!open) return null;

  async function onSubmit(event) {
    event.preventDefault();
    setStatus("submitting");
    setMessage("");
    try {
      const res = await fetch(`${API}/newsletter-subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "family-waitlist", language: lang }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || t.error);
      setStatus("success");
      setMessage(t.success);
      void trackAnalyticsEvent("newsletter_signup", { form_location: "family_waitlist" });
    } catch (err) {
      setStatus("error");
      setMessage(err.message || t.error);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="waitlist-modal-title"
    >
      <button
        type="button"
        aria-label={t.close}
        onClick={onClose}
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/30 bg-white/45 p-7 shadow-[0_30px_80px_-20px_rgba(15,28,68,0.45)] backdrop-blur-2xl sm:p-8">
        <button
          type="button"
          aria-label={t.close}
          onClick={onClose}
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-[#18306f]/70 transition hover:bg-[#18306f]/10 hover:text-[#18306f]"
        >
          <FiX className="h-5 w-5" aria-hidden />
        </button>
        <h2
          id="waitlist-modal-title"
          className="text-2xl font-extrabold leading-tight text-[#18306f] sm:text-3xl"
        >
          {t.title}
        </h2>
        <p className="mt-3 text-sm leading-6 text-[#405785] sm:text-base">{t.description}</p>
        {status === "success" ? (
          <p className="mt-6 rounded-2xl bg-[#dfe9ff] px-4 py-3 text-sm font-semibold text-[#18306f]">
            {message}
          </p>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-3">
            <input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t.emailPlaceholder}
              className="w-full rounded-full border border-[#d7e3f6] bg-white px-5 py-3 text-base text-[#18306f] placeholder:text-[#18306f]/40 focus:border-[#4285F4] focus:outline-none focus:ring-2 focus:ring-[#4285F4]/30"
              autoFocus
            />
            <label className="flex items-start gap-3 rounded-2xl bg-white/50 px-4 py-3 text-left text-sm leading-5 text-[#405785]">
              <input
                type="checkbox"
                checked={launchConsent}
                onChange={(event) => setLaunchConsent(event.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-[#4285F4]"
                required
              />
              <span>
                {t.consentLabel} {t.privacyPrefix}{" "}
                <Link
                  to="/datenschutzhinweis"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#18306f] underline-offset-2 hover:underline"
                >
                  {t.privacyLinkLabel}
                </Link>
                .
              </span>
            </label>
            <button
              type="submit"
              disabled={status === "submitting"}
              className="inline-flex items-center justify-center rounded-full bg-[#4285F4] px-5 py-3 text-base font-semibold text-white shadow-md transition hover:bg-[#357ae8] disabled:cursor-wait disabled:opacity-80"
            >
              {status === "submitting" ? t.submitting : t.submit}
            </button>
            {status === "error" && message ? (
              <p className="text-sm font-medium text-[#b42318]">{message}</p>
            ) : null}
          </form>
        )}
      </div>
    </div>
  );
}
